import { c as _c } from "react/compiler-runtime";
import { ReactElement, memo, useContext } from 'react';
import { BAZAARVOICE_REVIEW_SECTION_ID } from '@spa/components/bazaarvoice/bazaarvoiceReviewSectionAnchor';
import ImageComponent from '@spa/components/image-component/image.component';
import { LocaleContext } from '@spa/lib/react/context/LocaleContext';
import { NamespacedIntl } from '@spa/lib/i18n/common/NamespacedIntl';
import formatRatingValue from '@spa/lib/formatRatingValue';
import getRoundedStarRating from '@spa/lib/getRoundedStarRating';
import { navigateToAccordionAnchor } from '@spa/components/accordion/accordion.component';
import styles from './product-ratings.component.module.scss';
import useNamespacedIntl from '@spa/lib/i18n/client/hooks/useNamespacedIntl';
export interface ProductRatingsComponentProps {
  readonly averageRating: number | null;
  readonly reviewCount: number | null;
  readonly enableReviewNavigation?: boolean;
}
interface ProductRatingsContentProps {
  readonly averageRating: number;
  readonly reviewCount: number;
  readonly enableReviewNavigation: boolean;
}
const ProductRatingsContent = t0 => {
  const $ = _c(30);
  const {
    averageRating,
    reviewCount,
    enableReviewNavigation
  } = t0;
  const {
    formatMessage
  } = useNamespacedIntl("product-ratings");
  const locale = useContext(LocaleContext);
  let t1;
  if ($[0] !== averageRating || $[1] !== locale) {
    t1 = formatRatingValue(averageRating, locale);
    $[0] = averageRating;
    $[1] = locale;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const formattedRating = t1;
  const roundedRating = getRoundedStarRating(averageRating, 5);
  const filledStars = Math.floor(roundedRating);
  const hasHalfStar = roundedRating - filledStars === 0.5;
  let stars;
  if ($[3] !== filledStars || $[4] !== hasHalfStar) {
    stars = [];
    for (let star = 1; star <= 5; star++) {
      let iconKey = "star-rating-empty";
      if (star <= filledStars) {
        iconKey = "star-rating-full";
      } else {
        if (hasHalfStar && star === filledStars + 1) {
          iconKey = "star-rating-half";
        }
      }
      stars.push(<ImageComponent key={star} src={`/assets/icons/${iconKey}.svg`} height={16} width={16} alt="" className={styles["product-rating__star"]} />);
    }
    $[3] = filledStars;
    $[4] = hasHalfStar;
    $[5] = stars;
  } else {
    stars = $[5];
  }
  const handleNavigateToReviews = _temp;
  let t2;
  if ($[6] !== stars) {
    t2 = <div className={styles["product-rating__stars"]}>{stars}</div>;
    $[6] = stars;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== formattedRating) {
    t3 = <span className={styles["product-rating__count"]}>{formattedRating}</span>;
    $[8] = formattedRating;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== reviewCount) {
    t4 = <span className={styles["product-rating__count"]}>({reviewCount})</span>;
    $[10] = reviewCount;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t2 || $[13] !== t3 || $[14] !== t4) {
    t5 = <>{t2}{t3}{t4}</>;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const content = t5;
  if (!enableReviewNavigation) {
    let t6;
    if ($[16] !== formatMessage || $[17] !== formattedRating || $[18] !== reviewCount) {
      t6 = formatMessage("ariaLabel", {
        rating: formattedRating,
        reviewCount
      });
      $[16] = formatMessage;
      $[17] = formattedRating;
      $[18] = reviewCount;
      $[19] = t6;
    } else {
      t6 = $[19];
    }
    let t7;
    if ($[20] !== content || $[21] !== t6) {
      t7 = <div className={styles["product-rating"]} role="img" aria-label={t6}>{content}</div>;
      $[20] = content;
      $[21] = t6;
      $[22] = t7;
    } else {
      t7 = $[22];
    }
    return t7;
  }
  let t6;
  if ($[23] !== formatMessage || $[24] !== formattedRating || $[25] !== reviewCount) {
    t6 = formatMessage("reviewsLinkAriaLabel", {
      rating: formattedRating,
      reviewCount
    });
    $[23] = formatMessage;
    $[24] = formattedRating;
    $[25] = reviewCount;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  let t7;
  if ($[27] !== content || $[28] !== t6) {
    t7 = <button type="button" className={styles["product-rating"]} onClick={handleNavigateToReviews} aria-label={t6}>{content}</button>;
    $[27] = content;
    $[28] = t6;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  return t7;
};
const ProductRatingsComponent = t0 => {
  const $ = _c(4);
  const {
    averageRating,
    reviewCount,
    enableReviewNavigation: t1
  } = t0;
  const enableReviewNavigation = t1 === undefined ? false : t1;
  let productRatings = null;
  if (averageRating !== null && reviewCount !== null) {
    let t2;
    if ($[0] !== averageRating || $[1] !== enableReviewNavigation || $[2] !== reviewCount) {
      t2 = <ProductRatingsContent averageRating={averageRating} reviewCount={reviewCount} enableReviewNavigation={enableReviewNavigation} />;
      $[0] = averageRating;
      $[1] = enableReviewNavigation;
      $[2] = reviewCount;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    productRatings = t2;
  }
  return productRatings;
};
export default memo(ProductRatingsComponent);
function _temp() {
  navigateToAccordionAnchor(BAZAARVOICE_REVIEW_SECTION_ID);
}